import { AvailableBatch, OpsLaborEntry, OpsStationConnection } from '@services/ops.service'

export interface SanityCandidate {
    /** Entry id when editing — used to exclude self from the unusual-station scan. Omit when adding. */
    id?: string | null
    clockInIso: string
    /** null means active / no end yet */
    clockOutIso: string | null
    /** Station the entry will be saved against. Omit / pass null for activity entries. */
    stationId?: string | null
    /** Display name of the candidate station — used purely for the warning copy. */
    stationName?: string | null
    /** Display name of the batch (job) the candidate is logged against — used for the warning copy. */
    batchNumber?: string | null
    /** Product name the batch belongs to — used for the off-process-flow warning copy. */
    productName?: string | null
    /**
     * Whether the candidate's station is flagged as allowing concurrent labor (e.g. Diss/Clean
     * lines where a job legitimately runs at two stations at once). Suppresses the
     * "active elsewhere" warning when the other active station also allows concurrent work.
     */
    stationAllowsConcurrent?: boolean
}

export interface UnusualStation {
    stationId: string
    stationName: string | null
}

export interface StationOffProcessFlow {
    /** Candidate (destination) station — where the operator was trying to clock in. */
    stationId: string
    stationName: string | null
    /** The batch's most recent known station — the "from" side of the attempted transition. */
    fromStationId: string | null
    fromStationName: string | null
    batchNumber: string | null
    productName: string | null
}

export interface JobActiveAtOtherStation {
    stationId: string
    stationName: string | null
}

/**
 * One station a batch is currently routed to. Mirrors the loaded AvailableBatch row, but only
 * carries the fields the sanity checks read so callers don't have to drag in the full type.
 */
export interface BatchStationInstance {
    batchEventId: string
    stationId: string
    stationName: string | null
    /** Passthrough of AvailableBatch.status. 'running' = an open batch_event at this station. */
    status: 'running' | 'idle' | 'completed' | 'aborted'
    /** When the batch's machine_event at this station started — used to pick the most recent
     *  "from" station for the off-process-flow warning copy. Optional; falls back to declaration order. */
    startedAt?: string | null
    /** Whether this station is flagged as allowing concurrent labor. See SanityCandidate.stationAllowsConcurrent. */
    allowConcurrent?: boolean
}

export interface StationLink {
    fromStationId: string
    toStationId: string
}

export interface JobRoutingContext {
    /**
     * Known instances of the candidate's batch. Today this is sourced from
     * `get_available_batches_for_site`, which dedupes by batch_number and returns only the
     * newest machine_event — so in practice this is a single-entry array representing the
     * batch's latest known station. The algorithm handles plural correctly if a future data
     * source surfaces all of the batch's station instances.
     */
    batchInstances: BatchStationInstance[]
    /** Site-level station connections. Walked forward only (from → to) for the off-process-flow check. */
    connections: StationLink[]
    /**
     * The batch_event_id of the entry being edited — excluded from BOTH the "active elsewhere"
     * and the off-process-flow scans. Without this, moving an Edit Shift entry from station S → T
     * would flag S as "active elsewhere" because the entry's own original batch_event is still
     * open at S, and would treat S as the job's latest routed station so the S → T correction
     * looks like an off-flow transition.
     */
    excludeBatchEventId?: string | null
}

export interface SanityResult {
    /** True when start and end fall on different local calendar days. Always false for active (no end) candidates. */
    spansMultipleDays: boolean
    /** Non-null when the user has prior history at other stations but none at the candidate's station. */
    unusualStation: UnusualStation | null
    /**
     * Non-null when the candidate station is neither one of the batch's known stations nor a
     * direct forward (from → to) neighbor of one of them in the site's connection graph.
     */
    stationOffProcessFlow: StationOffProcessFlow | null
    /** Every other station (besides the candidate's) where the same batch has an open instance. Empty when there are no conflicts. */
    jobActiveAtOtherStations: JobActiveAtOtherStation[]
    /** Local YYYY-MM-DD start date when known, used for the warning copy */
    startDateLabel: string | null
    /** Local YYYY-MM-DD end date when known, used for the warning copy */
    endDateLabel: string | null
    /** Hours of duration when known, otherwise null */
    durationHours: number | null
}

/**
 * Two timestamps "span multiple calendar days" when they land on different local YYYY-MM-DD dates.
 * Catches an 11pm-Monday to 12:30am-Tuesday entry just as much as a 25-hour shift.
 */
export function spansMultipleCalendarDays(candidate: SanityCandidate): boolean {
    if (!candidate.clockOutIso) return false
    const startKey = toLocalDateKey(candidate.clockInIso)
    const endKey = toLocalDateKey(candidate.clockOutIso)
    if (!startKey || !endKey) return false
    return startKey !== endKey
}

function toLocalDateKey(iso: string): string | null {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

export function durationHours(candidate: SanityCandidate): number | null {
    if (!candidate.clockOutIso) return null
    const start = parseIsoMs(candidate.clockInIso)
    const end = parseIsoMs(candidate.clockOutIso)
    if (start === null || end === null || end < start) return null
    return (end - start) / (60 * 60 * 1000)
}

/**
 * Flags a candidate station as unusual when the operator has prior history at OTHER stations
 * but none at this one. `stationHistory` is intended to be a fixed rolling-30-day window of
 * the operator's labor entries. Returns null when the history is empty (brand-new operators
 * aren't spammed).
 */
export function findUnusualStation(
    candidate: SanityCandidate,
    stationHistory: OpsLaborEntry[]
): UnusualStation | null {
    if (!candidate.stationId) return null
    const history = stationHistory.filter(e => (!candidate.id || e.id !== candidate.id) && !!e.station_id)
    if (history.length === 0) return null
    const everAtThisStation = history.some(e => e.station_id === candidate.stationId)
    if (everAtThisStation) return null
    return { stationId: candidate.stationId, stationName: candidate.stationName ?? null }
}

/**
 * The candidate station is "in flow" when it is one the batch has already been at (rework /
 * parallel work at the same station), or it is DIRECTLY wired to one of those stations in the
 * site's connection graph — in either direction. Anything else is off-flow and gets surfaced
 * as a warning.
 *
 * Adjacency is undirected for this check. Connections are stored directionally (from → to), but
 * a direct edge means the two stations are immediate neighbors in the flow regardless of travel
 * direction: stepping onto the immediately-next station (forward) and onto the immediately-previous
 * station (rework / backtrack) are both legitimate, so neither should fire.
 *
 * Direct neighbors only — multi-hop reachability would treat the whole connected component as
 * "in flow" and never fire for a site that has any process flow defined, defeating the point
 * of the check.
 *
 * Null-safe: skipped when the candidate has no station (activity entry), when the batch has
 * no known stations yet, or when no connection graph is modelled at all (we can't validate
 * routing the site never set up).
 *
 * Pass excludeBatchEventId to drop the entry being edited from the source-station scan — the
 * same exclusion the active-elsewhere check uses. Correcting an entry from its own accidental
 * wrong station to the intended one would otherwise treat that wrong station as the job's
 * latest routed station and flag the correction as an off-flow transition.
 */
export function findStationOffProcessFlow(
    candidate: SanityCandidate,
    batchInstances: BatchStationInstance[],
    connections: StationLink[],
    excludeBatchEventId?: string | null
): StationOffProcessFlow | null {
    if (!candidate.stationId) return null
    const instances = excludeBatchEventId
        ? batchInstances.filter(b => b.batchEventId !== excludeBatchEventId)
        : batchInstances
    if (instances.length === 0) return null
    if (connections.length === 0) return null

    const knownStationIds = new Set(
        instances.map(b => b.stationId).filter((id): id is string => !!id)
    )

    if (knownStationIds.has(candidate.stationId)) return null

    for (const conn of connections) {
        // Forward: a batch station wires directly to the candidate (known → candidate).
        if (knownStationIds.has(conn.fromStationId) && conn.toStationId === candidate.stationId) {
            return null
        }
        // Backward: the candidate wires directly to a batch station (candidate → known).
        if (knownStationIds.has(conn.toStationId) && conn.fromStationId === candidate.stationId) {
            return null
        }
    }

    const fromInstance = pickLatestInstance(instances)
    return {
        stationId: candidate.stationId,
        stationName: candidate.stationName ?? null,
        fromStationId: fromInstance?.stationId ?? null,
        fromStationName: fromInstance?.stationName ?? null,
        batchNumber: candidate.batchNumber ?? null,
        productName: candidate.productName ?? null
    }
}

/**
 * The batch's most recent known station. Prefers the instance with the latest startedAt;
 * falls back to the last item in declaration order when timestamps are missing.
 */
function pickLatestInstance(instances: BatchStationInstance[]): BatchStationInstance | null {
    if (instances.length === 0) return null
    let latest = instances[0]
    let latestMs: number | null = parseIsoMs(latest.startedAt)
    for (let i = 1; i < instances.length; i++) {
        const candidate = instances[i]
        const candidateMs = parseIsoMs(candidate.startedAt)
        if (candidateMs === null) {
            if (latestMs === null) latest = candidate
            continue
        }
        if (latestMs === null || candidateMs > latestMs) {
            latest = candidate
            latestMs = candidateMs
        }
    }
    return latest
}

/**
 * Every other station (besides the candidate's) where the same batch has an open instance.
 * Pass excludeBatchEventId to skip the entry being edited — its own original batch_event
 * would otherwise show up in this list against its pre-edit location.
 *
 * Concurrent-labor exception: an active instance is NOT flagged when both the candidate's
 * station and that instance's station are marked allow-concurrent. This is the Diss/Clean
 * case — a job legitimately runs at both a disassembly and a clean station at once. Validity
 * of the transition itself is left to the off-process-flow check; this only governs whether
 * simultaneous active labor is expected. Both sides must opt in: a concurrent-friendly station
 * overlapping with a normal one still warns.
 */
export function findJobActiveAtOtherStations(
    candidate: SanityCandidate,
    batchInstances: BatchStationInstance[],
    excludeBatchEventId?: string | null
): JobActiveAtOtherStation[] {
    if (!candidate.stationId) return []
    const matches: JobActiveAtOtherStation[] = []
    const seenStationIds = new Set<string>()
    for (const instance of batchInstances) {
        if (instance.status !== 'running') continue
        if (instance.stationId === candidate.stationId) continue
        if (excludeBatchEventId && instance.batchEventId === excludeBatchEventId) continue
        if (candidate.stationAllowsConcurrent && instance.allowConcurrent) continue
        if (seenStationIds.has(instance.stationId)) continue
        seenStationIds.add(instance.stationId)
        matches.push({ stationId: instance.stationId, stationName: instance.stationName })
    }
    return matches
}

/**
 * @param stationHistory   Operator's rolling last-30-days entries used for the unusual-station check.
 *                         Defaults to an empty array — when omitted, no station warning is produced.
 * @param routing          Batch routing context used for the off-process-flow and active-elsewhere
 *                         checks. When omitted those checks simply don't fire.
 */
export function evaluateSanity(
    candidate: SanityCandidate,
    stationHistory: OpsLaborEntry[] = [],
    routing?: JobRoutingContext
): SanityResult {
    return {
        spansMultipleDays: spansMultipleCalendarDays(candidate),
        unusualStation: findUnusualStation(candidate, stationHistory),
        stationOffProcessFlow: routing
            ? findStationOffProcessFlow(candidate, routing.batchInstances, routing.connections, routing.excludeBatchEventId)
            : null,
        jobActiveAtOtherStations: routing
            ? findJobActiveAtOtherStations(candidate, routing.batchInstances, routing.excludeBatchEventId)
            : [],
        startDateLabel: toLocalDateKey(candidate.clockInIso),
        endDateLabel: candidate.clockOutIso ? toLocalDateKey(candidate.clockOutIso) : null,
        durationHours: durationHours(candidate)
    }
}

export function hasSanityWarnings(result: SanityResult): boolean {
    return result.spansMultipleDays
        || !!result.unusualStation
        || !!result.stationOffProcessFlow
        || result.jobActiveAtOtherStations.length > 0
}

/**
 * Shared helper for building a JobRoutingContext from the data the modal call sites already load.
 * Centralizes the AvailableBatch[] → BatchStationInstance[] and OpsStationConnection[] → StationLink[]
 * shape mapping so the same transform doesn't drift across Add/Edit/Join callers.
 */
export function buildJobRoutingContext(
    batches: AvailableBatch[],
    connections: OpsStationConnection[],
    batchNumber: string,
    excludeBatchEventId?: string | null
): JobRoutingContext {
    const batchInstances: BatchStationInstance[] = batches
        .filter(b => b.batch_number === batchNumber)
        .map(b => ({
            batchEventId: b.batch_event_id,
            stationId: b.equipment_id,
            stationName: b.equipment_name ?? null,
            status: b.status,
            startedAt: b.started_at ?? null,
            allowConcurrent: b.allow_concurrent_jobs ?? false
        }))
    const links: StationLink[] = connections.map(c => ({
        fromStationId: c.from_station_id,
        toStationId: c.to_station_id
    }))
    return {
        batchInstances,
        connections: links,
        excludeBatchEventId: excludeBatchEventId ?? null
    }
}

function parseIsoMs(value: string | null | undefined): number | null {
    if (!value) return null
    const ms = Date.parse(value)
    return Number.isNaN(ms) ? null : ms
}
